import {
	Button,
	Content,
	EmptyState,
	EmptyStateBody,
	Level,
	LevelItem,
	Skeleton,
	Stack,
	StackItem,
	Truncate,
} from "@patternfly/react-core";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { type FC, useMemo } from "react";
import WebinarIcon from "../../../assets/Icon-Red_Hat-Laptop_Webinar-A-Red-RGB.png";
import type { IMBItemsEntity } from "../../../constants/apiconstants-drupal";
import type { DragHandleProps } from "../../../constants/CardsStructure";
import type { MKTContent } from "../../../constants/constants";
import Env from "../../../utils/env";
import { DashboardCard } from "../shared/DashboardCard";

const WebinarsCardContent: FC = () => {
	const queryClient = useQueryClient();
	const isMBLoading = queryClient.getQueryState(["marketingBlockData"])?.fetchStatus === "fetching";
	const webinarContent = queryClient.getQueryData<MKTContent>(["marketingBlockData"])?.webinar || null;
	const webinarsList = useMemo<IMBItemsEntity[]>(() => {
		return (
			webinarContent?.translations
				?.find((item) => item?.langcode === "en")
				?.field_values?.items?.sort((a, b) => b.timestamp - a.timestamp)
				?.slice(0, 3) || []
		);
	}, [webinarContent]);

	if (isMBLoading) {
		return (
			<Stack style={{ height: "100%", width: "100%" }} hasGutter>
				<StackItem>
					<Skeleton height="100%" width="100%" />
				</StackItem>
				<StackItem>
					<Skeleton height="100%" width="100%" />
				</StackItem>
				<StackItem>
					<Skeleton height="100%" width="100%" />
				</StackItem>
			</Stack>
		);
	}
	if (webinarsList?.length === 0) {
		return (
			<EmptyState>
				<EmptyStateBody>No webinars available</EmptyStateBody>
			</EmptyState>
		);
	}
	return (
		<Stack hasGutter>
			{webinarsList?.map((item, index) => {
				const eventTimestampMs = item.timestamp * 1000;
				const meetingDate = new Date();
				meetingDate.setTime(eventTimestampMs);
				const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles";
				const formattedTime = `${format(meetingDate, "MMMM d, yyyy | h:mmaaa")} ${timeZone}`;
				const { buttonLabel, dateString } =
					eventTimestampMs > Date.now()
						? { buttonLabel: "Attend webinar", dateString: formattedTime }
						: { buttonLabel: "Access webinar", dateString: "Available on demand" };
				return (
					<StackItem key={`key${index}`}>
						<Stack>
							<StackItem isFilled={true}>
								<Content>
									<b style={{ fontSize: "15px" }}>
										<Truncate content={item?.title} />
									</b>
								</Content>
							</StackItem>
							<StackItem>
								<Level>
									<LevelItem>
										<Content>
											<Content component="p" style={{ fontSize: "14px" }}>
												{dateString}
											</Content>
										</Content>
									</LevelItem>
									<LevelItem>
										<Button component="a" variant="link" href={item?.url}>
											{buttonLabel}
										</Button>
									</LevelItem>
								</Level>
								<hr style={{ height: "0.5px", backgroundColor: "grey", border: "none" }} />
							</StackItem>
						</Stack>
					</StackItem>
				);
			})}
		</Stack>
	);
};

export const WebinarsCard: FC<{ dragHandleProps?: DragHandleProps | undefined }> = ({ dragHandleProps }) => (
	<DashboardCard
		icon={WebinarIcon}
		title="Webinars"
		footerLinks={[
			{ label: "View all webinars", href: `${Env.ConnectPortalUrls[Env.getEnvName()]}en/partner-with-us/events` },
		]}
		cardBody={<WebinarsCardContent />}
		dragHandleProps={dragHandleProps}
	/>
);
